<script lang="ts">
  // Hand.svelte — minha mao de cartas. Renderiza SO as nao-jogadas
  // (regra Leonardo 2026-05-18: "ele jogou na mesa some da mao").
  // Cartas jogadas aparecem na area de Plays no centro.
  //
  // Encoberta (2026-05-18): a partir da 2a vaza o jogador pode marcar
  // "esconder próxima" pra que a proxima carta jogada va face-down.
  // Carta encoberta vale 0 — perde pra qualquer face-up adversaria.
  // No 1o trick a regra canonica PROIBE encobrir; o toggle so aparece
  // a partir do 2o. Backend tambem rejeita via ErrFlipNotAllowedInFirstTrick
  // (defesa em profundidade).
  import type { Card as CardType, MatchSnapshot } from '../../stores/match';
  import Card from './Card.svelte';
  import { send } from '../../stores/ws';
  import { isFirstTrick } from '../../lib/cards';
  import { sendCard } from '../../lib/cardTransition';

  type Props = {
    cards?: CardType[];
    vira?: CardType | null;
    canPlay: boolean;
    snap?: MatchSnapshot | null;
  };
  let { cards = [], vira, canPlay, snap = null }: Props = $props();

  // Filter primeiro — so renderizamos cartas nao-jogadas. Cards jogadas
  // viajam pra Plays (no centro). Hand fica vazia no fim da mao.
  let unplayed = $derived(cards.filter((c) => !c.played));
  let firstTrick = $derived(isFirstTrick(snap));
  // roundNumber do snap pra forcar re-mount no inicio de cada mao,
  // disparando anim de distribuicao das cartas.
  let roundNumber = $derived(snap?.round_number ?? 0);

  let playing = $state(false);
  let coverNext = $state(false);

  // Se o usuario marcou "esconder" mas o trick voltou a ser o 1o (nova
  // mao), desmarca pra UI nao mentir.
  $effect(() => {
    if (firstTrick && coverNext) coverNext = false;
  });

  // Toggle do botao "esconder": ignora clique no 1o trick (visualmente
  // disabled, mas defensivo aqui tambem).
  function toggleCover() {
    if (firstTrick) return;
    coverNext = !coverNext;
  }

  // Texto e tooltip do botao adaptam ao estado:
  // - 1o trick: "esconder (2ª vaza+)" disabled, explica a regra
  // - apos 1o: "esconder próxima" / "PRÓXIMA COBERTA ✓" quando ativo
  let coverLabel = $derived(
    firstTrick
      ? 'esconder (2ª vaza+)'
      : coverNext
        ? 'PRÓXIMA COBERTA ✓'
        : 'esconder próxima',
  );
  let coverTitle = $derived(
    firstTrick
      ? 'regra do truco: encoberta só a partir da 2ª vaza'
      : 'próxima carta vai virada (face para baixo) — perde sempre, serve pra blefar',
  );

  function handlePlay(card: CardType) {
    if (!canPlay || playing) return;
    if (!card.value || !card.suit) return;
    playing = true;
    // Reseta o gate apos 2s (safety net se servidor nao responder)
    setTimeout(() => (playing = false), 2000);
    // Forca face-up no 1o trick mesmo se algo deixou coverNext=true.
    const flipped = !firstTrick && coverNext;
    send('game:play', {
      card: `${card.value}${card.suit}`,
      flipped,
    });
    coverNext = false;
  }
</script>

<div class="hand-wrap">
  <div class="hand" class:my-turn={canPlay}>
    {#key roundNumber}
      {#each unplayed as card, i (`${card.value}${card.suit}`)}
        <div
          class="slot card-deal"
          style="animation-delay: {i * 110}ms"
          out:sendCard={{ key: `${card.value}${card.suit}` }}
        >
          <Card
            {card}
            {vira}
            playable={canPlay && !playing}
            size="lg"
            onClick={() => handlePlay(card)}
          />
        </div>
      {/each}
    {/key}
    {#if unplayed.length === 0}
      <div class="empty-hand">— sem cartas —</div>
    {/if}
  </div>

  <!-- Olho de "esconder proxima" com label. Olho fechado + glow amarelo
       quando ativo. Disabled no 1o trick (regra do truco: encoberta so
       a partir da 2a vaza). Felipe 2026-05-20. -->
  <button
    type="button"
    class="eye-toggle"
    class:active={coverNext && !firstTrick}
    class:locked={firstTrick}
    disabled={firstTrick}
    onclick={toggleCover}
    aria-pressed={coverNext && !firstTrick}
    aria-label={coverTitle}
    title={coverTitle}
  >
    {#if coverNext && !firstTrick}
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path d="M2 12c2-4 6-7 10-7 2.2 0 4.3.9 6 2.3l-1.5 1.5C15.2 8 13.6 7.5 12 7.5c-3.5 0-6.7 2.5-8.5 5 .7 1 1.5 1.9 2.5 2.7L4.5 16.8C3.4 15.9 2.6 14.8 2 12zM22 12c-2 4-6 7-10 7-2.2 0-4.3-.9-6-2.3l1.5-1.5C8.8 16 10.4 16.5 12 16.5c3.5 0 6.7-2.5 8.5-5-.7-1-1.5-1.9-2.5-2.7L19.5 7.2C20.6 8.1 21.4 9.2 22 12zM3 3l18 18-1.4 1.4-3-3C15.4 19.8 13.8 20 12 20c-4.5 0-8.5-3-10.5-7 .9-1.8 2.2-3.4 3.7-4.6L1.6 4.4 3 3z" fill="currentColor"/>
      </svg>
    {:else}
      <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true">
        <path d="M12 5C7 5 3 8.5 1 12c2 3.5 6 7 11 7s9-3.5 11-7c-2-3.5-6-7-11-7zm0 11.5c-2.5 0-4.5-2-4.5-4.5S9.5 7.5 12 7.5s4.5 2 4.5 4.5-2 4.5-4.5 4.5zm0-7a2.5 2.5 0 100 5 2.5 2.5 0 000-5z" fill="currentColor"/>
      </svg>
    {/if}
    <span class="eye-lbl">
      {#if firstTrick}
        2ª vaza+
      {:else if coverNext}
        coberta ✓
      {:else}
        esconder
      {/if}
    </span>
  </button>
</div>

<style>
  .hand-wrap {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    width: 100%;
  }
  .eye-toggle {
    background: rgba(0, 0, 0, 0.35);
    border: 1px solid rgba(180, 210, 255, 0.35);
    color: rgba(180, 210, 255, 0.75);
    padding: 4px 12px;
    border-radius: 999px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    gap: 6px;
    transition: background 0.15s, border-color 0.15s, color 0.15s, box-shadow 0.15s;
  }
  .eye-lbl {
    font-family: ui-monospace, "SFMono-Regular", "Menlo", monospace;
    font-size: 10px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    line-height: 1;
  }
  .eye-toggle:hover {
    border-color: rgba(255, 210, 74, 0.6);
    color: rgba(255, 210, 74, 0.9);
  }
  .eye-toggle.active {
    background: rgba(255, 210, 74, 0.18);
    border-color: rgba(255, 210, 74, 0.85);
    color: #ffd24a;
    box-shadow: 0 0 10px rgba(255, 210, 74, 0.45);
  }
  .eye-toggle.locked,
  .eye-toggle:disabled {
    border-color: rgba(180, 210, 255, 0.15);
    color: rgba(180, 210, 255, 0.3);
    cursor: not-allowed;
    background: rgba(0, 0, 0, 0.2);
    box-shadow: none;
  }
  .eye-toggle.locked:hover,
  .eye-toggle:disabled:hover {
    border-color: rgba(180, 210, 255, 0.15);
    color: rgba(180, 210, 255, 0.3);
  }

  .hand {
    display: flex;
    justify-content: center;
    align-items: flex-end;
    gap: clamp(6px, 1.5vw, 10px);
    padding: clamp(4px, 1vh, 10px);
    transition: gap 0.2s;
    /* Bug Felipe 2026-05-19: a hand encolhia conforme cartas saiam
     * pra mesa — me-portrait subia/descia. Fix: min-height fixo na
     * altura de 1 carta lg + padding. Mantem o slot reservado
     * mesmo com 0 cartas.
     * 2026-05-20: min-width adicionado pelo mesmo motivo no eixo X
     * (avatar deslizava horizontalmente em 2v2 quando hand encolhia). */
    min-height: calc(var(--card-w-lg) * 1.5 + clamp(8px, 2vh, 20px));
    min-width: calc(var(--card-w-lg) * 3 + 30px);
  }
  .my-turn { gap: clamp(8px, 2vw, 14px); }

  .slot { display: flex; align-items: flex-end; }

  .empty-hand {
    color: var(--text-dim);
    font-size: 11px;
    letter-spacing: 1.5px;
    text-transform: uppercase;
    padding: 12px 0;
  }
</style>
